import type { ReactNode } from "react";
import { Icon, type IconKey } from "@core/assets";
import { cn } from "@core/utils";

interface ResultCardProps {
  tone: "success" | "warning" | "error";
  icon: IconKey;
  title: string;
  description: string;
  /** Onward action(s) — a link or a button, stacked full-width under the copy. */
  children?: ReactNode;
  className?: string;
}

/**
 * Terminal-state card for the unauthenticated screens: a haloed glyph, a heading, a line of copy
 * and an optional action. Used wherever a flow ends in an explanation rather than a form — email
 * verified/expired/invalid, invitation accepted/expired/invalid.
 *
 * Lives in core because two modules render it (`account` and `identity`) and it takes no domain
 * type; it was a private helper in the verification screen until the invitation flow needed the
 * same chrome, and duplicating it per module is exactly what the boundary rules are meant to stop.
 *
 * The halo reads from the FEEDBACK token triplet (icon + ring + ringOuter) that theme.css ships
 * for this, rather than pairing a raw primitive fill with a status-badge text colour.
 */
export function ResultCard({
  tone,
  icon,
  title,
  description,
  children,
  className,
}: ResultCardProps) {
  const halo = {
    success:
      "bg-(--feedback-success-ring-outer) text-(--feedback-success-icon) ring-(--feedback-success-ring)",
    warning:
      "bg-(--feedback-warning-ring-outer) text-(--feedback-warning-icon) ring-(--feedback-warning-ring)",
    error:
      "bg-(--feedback-error-ring-outer) text-(--feedback-error-icon) ring-(--feedback-error-ring)",
  }[tone];

  return (
    /* The frames' AuthCard chrome: 12px radius, 24px padding, 16px rhythm, 480px ceiling. */
    <div
      className={cn(
        "flex w-full max-w-120 flex-col items-center gap-md rounded-lg border border-(--border-default) bg-surface-card p-lg text-center shadow-md sm:p-xl",
        className
      )}
    >
      <span
        className={cn(
          "grid size-16 place-items-center rounded-full ring-8",
          halo
        )}
      >
        <Icon name={icon} size={32} />
      </span>
      <div className="flex flex-col gap-sm">
        {/* Display xs/SemiBold — the heading style the frames give an AuthCard title. */}
        <h1 className="display-xs-semibold text-(--text-primary)">{title}</h1>
        <p className="text-sm-regular text-(--text-secondary)">{description}</p>
      </div>
      {children}
    </div>
  );
}

/**
 * A `Link` styled as the primary action, for the onward step of a terminal card.
 *
 * Exported as a class string rather than a component because the onward step is a NAVIGATION and
 * must stay a real anchor (middle-clickable, copyable) — wrapping it would hide the router's
 * `Link`. Uses the same semantic action tokens as the `Button` primitive, and `min-h-11` keeps the
 * 44px touch target the 320px floor requires.
 */
export const ACTION_LINK =
  "inline-flex min-h-11 w-full items-center justify-center rounded-md px-md py-2.5 " +
  "text-sm-semibold text-(--action-primary-fg) shadow-xs transition-colors " +
  "bg-action-primary hover:bg-action-primary-hover " +
  "focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-2 outline-none";

export type { ResultCardProps };
